import { useEffect, useState, type ReactNode } from "react"
import { Link, router } from "@inertiajs/react"
import { AlertTriangle, Clock, LogOut } from "lucide-react"
import { Button, buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export type KioskProps = {
  driverName: string | null
  organizationName: string | null
  expiresAt: string | null
  signOutPath: string
  codeScreenPath: string
}

/** Below this the session is close enough to ending to interrupt for. */
const WARN_MS = 2 * 60 * 1000
/** Below this, seconds matter — make the countdown the loudest thing on screen. */
const URGENT_MS = 30 * 1000

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, "0")}`
}

/**
 * Chrome for every page inside an office-kiosk session (PRD §8.2, DESIGN.md §6):
 * full-bleed, no sidebar or nav, and a visible auto-logout timer.
 *
 * The timer is always on screen but deliberately quiet until it matters. A
 * permanent, prominent countdown would stand over a driver working through
 * legally binding paperwork, which is the sort of hostility DESIGN.md warns
 * against — so above two minutes it's a muted line, and only then does it
 * escalate into something that interrupts.
 */
export function KioskLayout({ kiosk, children }: { kiosk: KioskProps; children: ReactNode }) {
  const [remainingMs, setRemainingMs] = useState<number | null>(null)

  useEffect(() => {
    if (!kiosk.expiresAt) return
    const expiresAt = new Date(kiosk.expiresAt).getTime()
    const tick = () => setRemainingMs(Math.max(0, expiresAt - Date.now()))
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [kiosk.expiresAt])

  // The session is already gone server-side; a full page visit clears whatever
  // the previous driver had on screen along with it.
  useEffect(() => {
    if (remainingMs !== null && remainingMs <= 0) {
      window.location.assign(kiosk.codeScreenPath)
    }
  }, [remainingMs, kiosk.codeScreenPath])

  // Any request slides the idle window forward server-side, so re-fetching the
  // kiosk prop is itself what buys more time. Reading a long form generates no
  // requests, which is exactly when a driver would otherwise be timed out.
  function keepSignedIn() {
    router.reload({ only: ["kiosk"] })
  }

  const warning = remainingMs !== null && remainingMs <= WARN_MS
  const urgent = remainingMs !== null && remainingMs <= URGENT_MS

  return (
    <div className="flex min-h-svh flex-col bg-background">
      {warning && (
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-3 px-4 py-3",
            urgent ? "bg-destructive text-destructive-foreground" : "bg-warning text-warning-foreground",
          )}
        >
          <p className="flex items-center gap-2 font-medium">
            <AlertTriangle className="size-5 shrink-0" aria-hidden="true" />
            <span aria-hidden="true">
              Signing you out in{" "}
              <span className="font-mono tabular-nums">{formatRemaining(remainingMs)}</span>
            </span>
            {/* The countdown re-renders every second; announcing it live would
                repeat once a second. The alert says the thing that's actually
                new — and only says it twice, at each threshold. */}
            <span className="sr-only" role="alert">
              {urgent
                ? "You're about to be signed out. Choose Keep me signed in to stay."
                : "You'll be signed out in under two minutes. Choose Keep me signed in to stay."}
            </span>
          </p>
          <Button size="lg" variant="secondary" onClick={keepSignedIn}>
            Keep me signed in
          </Button>
        </div>
      )}

      <header className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-border px-4 py-3">
        <div className="min-w-0">
          {kiosk.driverName && <p className="truncate font-medium">Signed in as {kiosk.driverName}</p>}
          {kiosk.organizationName && (
            <p className="truncate text-sm text-muted-foreground">{kiosk.organizationName}</p>
          )}
        </div>

        <div className="ms-auto flex items-center gap-3">
          {/* Always visible per DESIGN.md §6, but muted while there's plenty of
              time — the banner above takes over once there isn't. */}
          {remainingMs !== null && !warning && (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="size-4 shrink-0" aria-hidden="true" />
              <span aria-hidden="true" className="font-mono tabular-nums">
                {formatRemaining(remainingMs)}
              </span>
              <span className="sr-only">
                {`You'll be signed out after ${formatRemaining(remainingMs)} without activity.`}
              </span>
            </p>
          )}
          <Link
            href={kiosk.signOutPath}
            method="delete"
            as="button"
            className={cn(buttonVariants({ size: "lg", variant: "outline" }))}
          >
            <LogOut className="size-4" aria-hidden="true" />
            I&rsquo;m done
          </Link>
        </div>
      </header>

      {/* min-w-0 so a wide child can't push the page sideways on the iPad. */}
      <main className="min-w-0 flex-1">{children}</main>
    </div>
  )
}
